import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import {
  AlertTriangle,
  CalendarClock,
  DollarSign,
  Package,
  ChevronDown,
  Check,
  Filter,
  Truck,
} from 'lucide-react';
import { ReportsService, type ExpiringProduct } from '../../services/reports/reports.service';
import { SuppliersService } from '../../services/suppliers/suppliers.service';

const formatMoney = (amount: number) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(amount);
};

const KPICard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}> = ({ title, value, subtitle, icon, iconBg, iconColor }) => (
  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-xl ${iconBg}`}>
        <div className={iconColor}>{icon}</div>
      </div>
    </div>
  </div>
);

const KPICardSkeleton = () => (
  <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
        <div className="h-8 bg-gray-200 rounded w-32" />
      </div>
      <div className="p-3 rounded-xl bg-gray-200 w-12 h-12" />
    </div>
  </div>
);

const DAYS_OPTIONS = [
  { value: 30, label: 'Próximos 30 días' },
  { value: 60, label: 'Próximos 60 días' },
  { value: 90, label: 'Próximos 90 días' },
  { value: 180, label: 'Próximos 6 meses' },
  { value: 365, label: 'Próximo año' },
];

export const PerdidasVencimientoContent: React.FC = () => {
  const [days, setDays] = useState<number>(90);
  const [proveedorId, setProveedorId] = useState<string>('');

  const { data: proveedores } = useQuery({
    queryKey: ['proveedores-dropdown-perdidas'],
    queryFn: async () => {
      const r = await SuppliersService.getAllForSelect();
      return r.success ? r.data : [];
    },
    staleTime: Infinity,
  });

  const query = useQuery({
    queryKey: ['expiring-products', days, proveedorId],
    queryFn: () => ReportsService.getExpiringProducts(days, proveedorId || undefined),
    staleTime: 5 * 60 * 1000,
  });

  const productos = query.data?.success ? query.data.data : [];

  const kpis = useMemo(() => {
    const totalLotes = productos.length;
    const totalUnidades = productos.reduce((sum, p) => sum + p.cantidad, 0);
    const valorTotal = productos.reduce((sum, p) => sum + p.valorPerdidaEstimado, 0);
    const productosDistintos = new Set(productos.map((p) => p.productoId)).size;
    const vencidos = productos.filter((p) => p.diasParaVencer <= 0).length;
    return { totalLotes, totalUnidades, valorTotal, productosDistintos, vencidos };
  }, [productos]);

  const isLoading = query.isLoading && !query.data;

  return (
    <div>
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Listbox value={days} onChange={setDays}>
          <div className="relative">
            <ListboxButton className="relative w-[200px] cursor-pointer rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
              <span className="block truncate">
                {DAYS_OPTIONS.find((d) => d.value === days)?.label}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>
            <ListboxOptions
              transition
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              {DAYS_OPTIONS.map((opt) => (
                <ListboxOption
                  key={opt.value}
                  value={opt.value}
                  className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                >
                  <span className="block truncate">{opt.label}</span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                    <Check className="h-4 w-4" />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        <Listbox value={proveedorId} onChange={setProveedorId}>
          <div className="relative">
            <ListboxButton className="relative w-[220px] cursor-pointer rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all">
              <span className="block truncate">
                {proveedorId ? proveedores?.find((p) => p.id === proveedorId)?.nombre : 'Todos los proveedores'}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
              </span>
            </ListboxButton>
            <ListboxOptions
              transition
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
            >
              <ListboxOption
                value=""
                className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
              >
                <span className="block truncate">Todos los proveedores</span>
                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                  <Check className="h-4 w-4" />
                </span>
              </ListboxOption>
              {proveedores?.map((p) => (
                <ListboxOption
                  key={p.id}
                  value={p.id}
                  className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-emerald-50 data-[focus]:text-emerald-900 transition-colors"
                >
                  <span className="block truncate">{p.nombre}</span>
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-emerald-600 opacity-0 group-data-[selected]:opacity-100">
                    <Check className="h-4 w-4" />
                  </span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>

        <button
          onClick={() => query.refetch()}
          className="p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          title="Recargar datos"
        >
          <Filter className="w-5 h-5" />
        </button>

        {proveedorId && (
          <button
            onClick={() => setProveedorId('')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Truck className="w-4 h-4 text-emerald-600" />
            <span className="truncate max-w-[150px]">
              {proveedores?.find((p) => p.id === proveedorId)?.nombre}
            </span>
            <span className="text-gray-400">×</span>
          </button>
        )}
      </div>

      {/* KPIs */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPICardSkeleton />
          <KPICardSkeleton />
          <KPICardSkeleton />
          <KPICardSkeleton />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPICard
            title="Lotes por Vencer"
            value={kpis.totalLotes.toString()}
            subtitle={`${kpis.vencidos} ya vencidos`}
            icon={<CalendarClock className="w-6 h-6" />}
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
          />
          <KPICard
            title="Unidades en Riesgo"
            value={kpis.totalUnidades.toLocaleString('es-CO')}
            subtitle="Cantidad total"
            icon={<Package className="w-6 h-6" />}
            iconBg="bg-blue-100"
            iconColor="text-blue-600"
          />
          <KPICard
            title="Valor Estimado de Pérdida"
            value={formatMoney(kpis.valorTotal)}
            subtitle={`${kpis.productosDistintos} productos distintos`}
            icon={<DollarSign className="w-6 h-6" />}
            iconBg="bg-red-100"
            iconColor="text-red-600"
          />
          <KPICard
            title="Productos Afectados"
            value={kpis.productosDistintos.toString()}
            subtitle="Diferentes referencias"
            icon={<AlertTriangle className="w-6 h-6" />}
            iconBg="bg-orange-100"
            iconColor="text-orange-600"
          />
        </div>
      )}

      {/* Tabla */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Detalle de Lotes por Vencer</h3>
          <span className="text-sm text-gray-500">
            {productos.length} registros
          </span>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : productos.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            No hay lotes por vencer en este período
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lote</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Presentación</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Precio Unit.</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor Pérdida</th>
                  <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase">Vencimiento</th>
                  <th className="px-5 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {productos.map((p) => (
                  <tr key={p.loteId} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="text-sm font-medium text-gray-900">{p.productoNombre}</p>
                      <p className="text-xs text-gray-500">{p.categoria}</p>
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-900 font-mono">{p.numeroLote}</td>
                    <td className="px-5 py-3 text-sm text-gray-500">{p.presentacionNombre || '—'}</td>
                    <td className="px-5 py-3 text-right text-sm font-medium text-gray-900">{p.cantidad}</td>
                    <td className="px-5 py-3 text-right text-sm text-gray-500">{formatMoney(p.precioUnitario)}</td>
                    <td className="px-5 py-3 text-right text-sm font-bold text-red-600">{formatMoney(p.valorPerdidaEstimado)}</td>
                    <td className="px-5 py-3 text-center text-sm text-gray-500">{p.fechaVencimiento}</td>
                    <td className="px-5 py-3 text-center">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          p.diasParaVencer <= 0
                            ? 'bg-red-100 text-red-700'
                            : p.diasParaVencer <= 30
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {p.diasParaVencer <= 0
                          ? `Vencido (${Math.abs(p.diasParaVencer)}d)`
                          : `${p.diasParaVencer} días`}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
